import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Key, Copy, Eye, EyeOff, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ApiKeys = ({ token }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('SpendLens Integration');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [showKey, setShowKey] = useState(false);

  const fetchKeys = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api-keys`, { headers: { Authorization: `Bearer ${token}` } });
      setKeys(res.data.keys || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCreate = async () => {
    if (!newLabel.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api-keys`, { label: newLabel }, { headers: { Authorization: `Bearer ${token}` } });
      setNewKey(res.data.api_key);
      setShowKey(true);
      toast.success('API key created. Copy it now — it won\'t be shown again.');
      fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create key');
    } finally { setCreating(false); }
  };

  const handleRevoke = async (keyId) => {
    if (!window.confirm('Revoke this API key? Connected apps will lose access.')) return;
    try {
      await axios.delete(`${API}/api-keys/${keyId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('API key revoked');
      fetchKeys();
    } catch { toast.error('Failed to revoke key'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const baseUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="space-y-5" data-testid="api-keys-page">
      <div>
        <h1 className="text-3xl font-heading font-bold text-[#0F172A] mb-1">API Keys</h1>
        <p className="text-slate-600 text-sm">Generate API keys for external integrations like SpendLens</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
          <Key size={18} className="text-slate-600" />
          <h2 className="text-base font-semibold text-slate-800">Your API Keys</h2>
        </div>

        <div className="p-4 space-y-4">
          {/* Create new key */}
          <div className="flex items-center gap-2">
            <input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Key label (e.g., SpendLens Production)"
              className="flex-1 px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="api-key-label-input"
            />
            <Button onClick={handleCreate} disabled={creating || !newLabel.trim()} size="sm"
              className="bg-[#0F172A] hover:bg-slate-800 text-white gap-1" data-testid="create-api-key-btn">
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Key size={14} />}
              Generate Key
            </Button>
          </div>

          {/* Newly created key display */}
          {newKey && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3" data-testid="new-key-display">
              <p className="text-xs font-semibold text-emerald-800 mb-2">New API Key (copy now — won't be shown again):</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white border border-emerald-200 rounded px-3 py-2 text-xs font-mono text-emerald-900 select-all">
                  {showKey ? newKey : '\u2022'.repeat(40)}
                </code>
                <button onClick={() => setShowKey(!showKey)} className="p-2 hover:bg-emerald-100 rounded" data-testid="toggle-key-visibility">
                  {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button onClick={() => copyToClipboard(newKey)} className="p-2 hover:bg-emerald-100 rounded" data-testid="copy-key-btn">
                  <Copy size={14} />
                </button>
              </div>
            </div>
          )}

          {/* Keys table */}
          {loading ? (
            <div className="py-6 text-center"><Loader2 size={20} className="animate-spin mx-auto text-slate-300" /></div>
          ) : keys.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">No API keys yet. Generate one to enable SpendLens integration.</p>
          ) : (
            <table className="w-full" data-testid="api-keys-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Label</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Key</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Created</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Last Used</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {keys.map(k => (
                  <tr key={k.id} className="hover:bg-slate-50" data-testid={`api-key-row-${k.id}`}>
                    <td className="px-3 py-2 text-sm text-slate-700 font-medium">{k.label}</td>
                    <td className="px-3 py-2 text-xs font-mono text-slate-500">{k.key_prefix}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${k.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                        {k.is_active ? 'Active' : 'Revoked'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{k.created_at ? new Date(k.created_at).toLocaleDateString() : '-'}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{k.last_used ? new Date(k.last_used).toLocaleDateString() : 'Never'}</td>
                    <td className="px-3 py-2">
                      {k.is_active && (
                        <button onClick={() => handleRevoke(k.id)} className="text-xs text-red-500 hover:text-red-700 font-medium flex items-center gap-1" data-testid={`revoke-key-${k.id}`}>
                          <Trash2 size={12} /> Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Integration Guide */}
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 space-y-3" data-testid="api-usage-guide">
            <p className="text-sm font-semibold text-slate-700">SpendLens Integration Guide</p>
            <div className="text-xs text-slate-500 space-y-2">
              <div>
                <p className="text-slate-600 font-medium mb-1">1. List available sessions:</p>
                <code className="block bg-white border border-slate-200 rounded px-3 py-2 font-mono break-all">
                  GET {baseUrl}/api/external/sessions<br/>
                  Header: X-API-Key: dr_...
                </code>
              </div>
              <div>
                <p className="text-slate-600 font-medium mb-1">2. Pull classified data (paginated):</p>
                <code className="block bg-white border border-slate-200 rounded px-3 py-2 font-mono break-all">
                  GET {baseUrl}/api/external/classified-data?upload_id=SESSION_ID&page=1&per_page=100<br/>
                  Header: X-API-Key: dr_...
                </code>
              </div>
              <p className="text-slate-500 mt-1">
                <span className="font-medium text-slate-600">Response fields:</span> id, supplier, description, amount, currency, date, category_l1-l4, payment_terms, taxonomy_type, is_split_transaction, extra columns
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
